/* eslint-disable no-unused-vars */
import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik, FormikProvider, FieldArray} from 'formik'
import classNames from 'classnames'
import Dropdown from 'react-bootstrap/Dropdown'

import {KTSVG, InvoiceType, TaxType, WitholdingType} from '../../../../helpers'
import {
  KTCard,
  KTCardBody,
  Select,
  SelectCreatable,
  InputPrice,
  Loading,
  Datepicker,
} from '../../../../components'
import {PageTitle} from '../../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  expenseinvoiceDetailGetId,
  expenseinvoiceDetailUpdate,
  setIsRedirect,
} from '../../../../redux/slices/expenseinvoice.slice'
import {
  projectLookup,
  allProductLookup,
  warehouseLookup,
  basicLookup,
  staffGetAll,
} from '../../../../redux/slices/lookup.slice'
import {productGetInfo, productCreateFast} from '../../../../redux/slices/product.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../../utils/performanceOptimizations'

const selectLookupData = (state) => ({
  productLookup: state.lookup.productLookup,
  projectLookupList: state.lookup.projectLookupList,
  warehouseLookupList: state.lookup.warehouseLookupList,
  ProductUnit: state.lookup.ProductUnit,
  DiscountType: state.lookup.DiscountType,
  staffLookup: state.lookup.staffLookup,
  InvoiceKind: state.lookup.InvoiceKind,
  loading: state.lookup.loading
})

const selectExpenseInvoiceData = (state) => ({
  isRedirect: state.expenseinvoice.isRedirect,
  detail: state.expenseinvoice.detail,
  loading: state.expenseinvoice.loading
})

const selectProductData = (state) => ({
  detail: state.product.detail,
  isRedirect: state.product.isRedirect
})

function Edit() {
  const intl = useIntl()
  const {id} = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const pageUrl = useMemoizedPageUrl(location)

  const {
    productLookup,
    projectLookupList,
    warehouseLookupList,
    ProductUnit,
    DiscountType,
    staffLookup,
    InvoiceKind,
    loading,
  } = useSelector(selectLookupData)
  const {isRedirect, detail, loading: updateLoading} = useSelector(selectExpenseInvoiceData)
  const {detail: product, isRedirect: createProduct} = useSelector(selectProductData)
  const dispatch = useDispatch()
  
  const formikRef = useRef(null)
  const isFormPopulatedRef = useRef(false)
  
  const [changeItem, setChangeItem] = useState()
  const [changeData, setChangeData] = useState()
  const [changeProduct, setChangeProduct] = useState()
  const [createItem, setCreateItem] = useState()
  const [visibiltyItem, setVisibiltyItem] = useState([
    {
      Discount: false,
      Description: false,
      WitholdingRate: false,
    },
  ])

  useEffect(() => {
    dispatch(expenseinvoiceDetailGetId({id}))
    dispatch(projectLookup())
    dispatch(allProductLookup())
    dispatch(warehouseLookup())
    dispatch(basicLookup('ProductUnit'))
    dispatch(basicLookup('DiscountType'))
    dispatch(staffGetAll())
    dispatch(basicLookup('InvoiceKind'))
  }, [dispatch, id])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ProjectId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    InvoiceKindId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    ExpenseInvoiceItems: Yup.array().of(
      Yup.object().shape({
        ProductId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        Amount: Yup.number()
          .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        UnitPrice: Yup.number()
          .typeError(intl.formatMessage({id: 'REQUIRED.VALID'}))
          .required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        ProductUnitId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        TaxId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
      })
    ),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Id: id,
    Name: '',
    ProjectId: null,
    EditDate: new Date(),
    DefermentDate: new Date(),
    InvoiceNumber: null,
    SubTotalPrice: 0,
    ExpenseFile: null,
    RelatedStaffId: '',
    TotalTax: 0,
    TotalDiscount: 0,
    GrossTotalPrice: 0,
    TotalWitholding: 0,
    TotalPrice: 0,
    InvoiceTypeId: 1,
    InvoiceKindId: '',
    ExpenseInvoiceItems: [
      {
        Id: 0,
        ProductId: null,
        WarehouseId: null,
        Amount: 1,
        UnitPrice: 0,
        ProductUnitId: 1,
        TaxId: 6,
        TotalTax: null,
        SubTotalPrice: 0,
        TotalPrice: 0,
        Description: null,
        DiscountTypeId: 1,
        DiscountRate: 0,
        Discount: null,
        WitholdingRateId: null,
        Witholding: null,
      },
    ],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting}) => {
    setSubmitting(true)
    dispatch(expenseinvoiceDetailUpdate(values))
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  useEffect(() => {
    formikRef.current = formik
  }, [formik])

  useEffect(() => {
    if (detail && formikRef.current && !isFormPopulatedRef.current) {
      for (const property in detail) {
        const propVal = property.charAt(0).toUpperCase() + property.slice(1)

        if (property == 'expenseInvoiceItemModels') {
          detail['expenseInvoiceItemModels'].map((item, index) => {
            for (const invoice in item) {
              const propValArr = invoice.charAt(0).toUpperCase() + invoice.slice(1)
              formikRef.current.setFieldValue(`ExpenseInvoiceItems[${index}][${propValArr}]`, item[invoice])
            }
          })
        }

        for (const values in formValues) {
          if (values === propVal) {
            formikRef.current.setFieldValue(
              propVal,
              property === 'editDate' || property === 'defermentDate'
                ? new Date(detail[property])
                : detail[property]
            )
          } else if (values === 'File') {
            formikRef.current.setFieldValue('File', detail['fileUrl'])
          }
        }
      }
      isFormPopulatedRef.current = true
    }
  }, [detail, formValues])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.EXPENSES'}),
      path: '/expenses/expenses',
      isSeparator: false,
      isActive: false,
    },
    {
      title: intl.formatMessage({id: 'PAGE_TITLE.DETAIL'}),
      path: `/expenses/expenses/detail/detail/${id}`,
      isSeparator: false,
      isActive: false,
    },
    {
      title: '',
      path: '',
      isSeparator: true,
      isActive: false,
    },
  ], [intl, id])

  useEffect(() => {
    if (isRedirect) {
      navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${id}`)
      dispatch(setIsRedirect(false))
    }
  }, [isRedirect, navigate, pageUrl, id, dispatch])

  const onChangeItem = useCallback((target, value, index) => {
    setChangeItem({index, target})
    formik.setFieldValue(`ExpenseInvoiceItems[${index}][${target}]`, value)
  }, [])

  const sumArrayItem = useCallback((item) => {
    return formik.values.ExpenseInvoiceItems.reduce((a, v) => (a = a + v[item]), 0)
  }, [formik.values.ExpenseInvoiceItems])

  useEffect(() => {
    const expenseInvoiceItems = formik.values.ExpenseInvoiceItems[changeItem && changeItem.index]

    if (expenseInvoiceItems) {
      const subTotalPrice = Number((expenseInvoiceItems.UnitPrice * expenseInvoiceItems.Amount).toFixed(4))
      const discount = Number((
        expenseInvoiceItems.DiscountTypeId == 1
          ? (subTotalPrice * expenseInvoiceItems.DiscountRate) / 100
          : expenseInvoiceItems.DiscountRate
      ).toFixed(4))
      const tax = TaxType.find((item) => item.id == expenseInvoiceItems.TaxId)
      const totalTax = Number((((subTotalPrice - discount) * (tax && tax.value || 0)) / 100).toFixed(4))
      const witholdingRate = WitholdingType.find(
        (item) => item.id == expenseInvoiceItems.WitholdingRateId
      )
      const witholding = Number(((totalTax * (witholdingRate && witholdingRate.value || 0)) / 100).toFixed(4))
      const totalPrice = Number((subTotalPrice + totalTax - (witholding + discount)).toFixed(4))

      formik.setFieldValue(`ExpenseInvoiceItems[${changeItem && changeItem.index}].SubTotalPrice`, subTotalPrice)
      formik.setFieldValue(`ExpenseInvoiceItems[${changeItem && changeItem.index}].Discount`, discount)
      formik.setFieldValue(`ExpenseInvoiceItems[${changeItem && changeItem.index}].TotalTax`, totalTax)
      formik.setFieldValue(`ExpenseInvoiceItems[${changeItem && changeItem.index}].Witholding`, witholding)
      formik.setFieldValue(`ExpenseInvoiceItems[${changeItem && changeItem.index}].TotalPrice`, totalPrice)

      setChangeData(totalPrice)
    }
  }, [changeItem])

  useEffect(() => {
    formik.setFieldValue('SubTotalPrice', sumArrayItem('SubTotalPrice'))
    formik.setFieldValue('TotalTax', sumArrayItem('TotalTax'))
    formik.setFieldValue('TotalDiscount', sumArrayItem('Discount'))
    formik.setFieldValue(
      'GrossTotalPrice',
      sumArrayItem('SubTotalPrice') - sumArrayItem('Discount')
    )
    formik.setFieldValue('TotalWitholding', sumArrayItem('Witholding'))
    formik.setFieldValue('TotalPrice', sumArrayItem('TotalPrice'))
  }, [changeData])

  const onClickNewItem = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      Id: 0,
      ProductId: null,
      WarehouseId: null,
      Amount: 1,
      UnitPrice: 0,
      ProductUnitId: 1,
      TaxId: 6,
      TotalTax: null,
      SubTotalPrice: 0,
      TotalPrice: 0,
      Description: null,
      DiscountTypeId: 1,
      DiscountRate: 0,
      Discount: null,
      WitholdingRateId: null,
      Witholding: null,
    })
    setVisibiltyItem([
      ...visibiltyItem,
      {
        Discount: false,
        Description: false,
        WitholdingRate: false,
      },
    ])
  }, [visibiltyItem])

  const onClickVisibityItem = useCallback((item, index) => {
    const newArr = [...visibiltyItem]
    newArr[index][item] = !newArr[index][item]
    setVisibiltyItem(newArr)
  }, [visibiltyItem])

  const onChangeProduct = useCallback((item, index) => {
    formik.setFieldValue(`ExpenseInvoiceItems[${index}].ProductId`, Number(item && item.value))
    setChangeProduct(index)
    dispatch(productGetInfo({Id: item && item.value}))
  }, [dispatch])

  useEffect(() => {
    if (product) {
      formik.setFieldValue(
        `ExpenseInvoiceItems[${changeProduct}].ProductUnitId`,
        product && product.productUnitId
      )
      formik.setFieldValue(
        `ExpenseInvoiceItems[${changeProduct}].UnitPrice`,
        product && product.baseSalePrice
      )
      formik.setFieldValue(`ExpenseInvoiceItems[${changeProduct}].TaxId`, product && product.taxId)
      setChangeItem({index: changeProduct, target: 'UnitPrice'})
    }
  }, [product, changeProduct])

  const onClickFastProduct = useCallback((Name, Index) => {
    dispatch(productCreateFast({Name}))
    setCreateItem({Name, Index})
  }, [dispatch])

  useEffect(() => {
    if (createProduct) dispatch(allProductLookup())
  }, [createProduct, dispatch])

  useEffect(() => {
    if (productLookup && createProduct) {
      formik.setFieldValue(`ExpenseInvoiceItems[${createItem && createItem.Index}].ProductId`, createProduct.id)
    }
  }, [productLookup, createProduct, createItem])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.EXPENSES.DETAIL.EDIT'})}
      </PageTitle>
      <div className='row gy-10 gx-xl-10'>
        <div className='col-xl-12'>
          <KTCard>
            <KTCardBody>
              <FormikProvider value={formik}>
                {detail && (
                  <form className='form' onSubmit={formik.handleSubmit} noValidate>
                    <div className='row mb-6'>
                      <label className='col-lg-3 required fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.NAME'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <input
                          type='text'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          required
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('Name')}
                        />
                        {formik.touched.Name && formik.errors.Name && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.Name}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.COMPANYID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <span className='fw-bold fs-6'>{detail.companyName}</span>
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.PROJECTID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Select
                          data={projectLookupList}
                          required
                          {...formik.getFieldProps('ProjectId')}
                          onChange={(item) =>
                            formik.setFieldValue('ProjectId', Number(item && item.value))
                          }
                        />
                      </div>
                      {formik.touched.ProjectId && formik.errors.ProjectId && (
                        <div className='fv-plugins-message-container'>
                          <div className='fv-help-block'>
                            <span role='alert'>{formik.errors.ProjectId}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                        {intl.formatMessage({id: 'SALEINVOICE.CREATE.INVOICEKINDNAME'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Select
                          data={InvoiceKind}
                          required
                          {...formik.getFieldProps('InvoiceKindId')}
                          onChange={(item) =>
                            formik.setFieldValue('InvoiceKindId', Number(item && item.value))
                          }
                        />
                        {formik.touched.InvoiceKind && formik.errors.InvoiceKind && (
                          <div className='fv-plugins-message-container'>
                            <div className='fv-help-block'>
                              <span role='alert'>{formik.errors.InvoiceKind}</span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.INVOICETYPEID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container d-flex gap-10'>
                        {InvoiceType.map((item, index) => {
                          return (
                            <div
                              className='form-check form-check-custom form-check-solid form-check-sm'
                              key={index}
                            >
                              <input
                                className='form-check-input'
                                name='InvoiceTypeId'
                                type='radio'
                                value={item.Id}
                                id={`InvoiceTypeId-${index}`}
                                onChange={() => formik.setFieldValue('InvoiceTypeId', item.Id)}
                                checked={item.Id === formik.values.InvoiceTypeId}
                              />
                              <label
                                className='form-check-label'
                                htmlFor={`InvoiceTypeId-${index}`}
                              >
                                {intl.formatMessage({id: item.Name})}
                              </label>
                            </div>
                          )
                        })}
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.EDITDATE'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('EditDate')}
                          onChange={([date]) => formik.setFieldValue('EditDate', date)}
                        />
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.DEFERMENTDATE'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Datepicker
                          {...formik.getFieldProps('DefermentDate')}
                          onChange={([date]) => formik.setFieldValue('DefermentDate', date)}
                        />
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.RELATEDSTAFFID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <Select
                          data={staffLookup}
                          {...formik.getFieldProps('RelatedStaffId')}
                          onChange={(item) =>
                            formik.setFieldValue('RelatedStaffId', Number(item && item.value))
                          }
                        />
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.INVOICENUMBER'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <input
                          type='text'
                          className='form-control form-control-solid mb-3 mb-lg-0'
                          autoComplete='off'
                          disabled={formik.isSubmitting}
                          {...formik.getFieldProps('InvoiceNumber')}
                        />
                      </div>
                    </div>

                    <div className='row mb-6'>
                      <label className='col-lg-3 fw-bold fs-6 mb-2'>
                        {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.FILEID'})}
                      </label>
                      <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                        <label
                          htmlFor='ExpenseFile'
                          className='btn btn-outline btn-outline-dashed btn-outline-dark btn-active-light-dark position-relative overflow-hidden'
                        >
                          <input
                            className='position-absolute z-index-2 opacity-0 w-1px h-1px'
                            type='file'
                            accept='image/*,.pdf,.doc,.docx,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.kmz,.kml,.ncz,.dwg'
                            id={'ExpenseFile'}
                            name={'ExpenseFile'}
                            onChange={(event) =>
                              formik.setFieldValue('ExpenseFile', event.currentTarget.files[0])
                            }
                          />
                          <KTSVG
                            path='/images/icons/duotune/communication/com008.svg'
                            className='svg-icon-3 position-relative z-index-1'
                          />
                          {intl.formatMessage({id: 'EXPENSEINVOICE.CREATEDETAIL.FILEID'})}
                        </label>
                      </div>
                    </div>

                    <FieldArray
                      name='ExpenseInvoiceItems'
                      render={(arrayHelpers) => (
                        <>
                          <div className='table-responsive'>
                            <table className='table g-5 gs-0 mb-0 fw-bold text-gray-700'>
                              <thead>
                                <tr className='border-bottom fs-7 fw-bold text-gray-700 text-uppercase'>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.PRODUCTID',
                                    })}
                                  </th>
                                  {formik.values.InvoiceTypeId === 1 && (
                                    <th>
                                      {intl.formatMessage({
                                        id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.WAREHOUSEID',
                                      })}
                                    </th>
                                  )}
                                  <th>
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.AMOUNT',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.PRODUCTUNITID',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.UNITPRICE',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.TAXID',
                                    })}
                                  </th>
                                  <th>
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.TOTALPRICE',
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>
                              </thead>
                              <tbody>
                                {formik.values.ExpenseInvoiceItems.map((item, index) => (
                                  <tr className='border-bottom border-bottom-dashed' key={index}>
                                    <td className='pe-7 ps-1'>
                                      <SelectCreatable
                                        data={productLookup}
                                        name={`ExpenseInvoiceItems[${index}].ProductId`}
                                        value={formik.values.ExpenseInvoiceItems[index].ProductId}
                                        required
                                        onChange={(item) => onChangeProduct(item, index)}
                                        onCreate={(item) => onClickFastProduct(item, index)}
                                      />

                                      {visibiltyItem[index] && visibiltyItem[index].Description && (
                                        <input
                                          type='text'
                                          className='form-control form-control-solid mt-2'
                                          autoComplete='off'
                                          disabled={formik.isSubmitting}
                                          placeholder={intl.formatMessage({
                                            id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.DESCRIPTION',
                                          })}
                                          name={`ExpenseInvoiceItems[${index}].Description`}
                                          value={
                                            formik.values.ExpenseInvoiceItems[index].Description
                                          }
                                          onChange={formik.handleChange}
                                        />
                                      )}
                                    </td>

                                    {formik.values.InvoiceTypeId === 1 && (
                                      <td className='ps-0'>
                                        <Select
                                          data={warehouseLookupList}
                                          name={`ExpenseInvoiceItems[${index}].WarehouseId`}
                                          value={
                                            formik.values.ExpenseInvoiceItems[index].WarehouseId
                                          }
                                          required
                                          onChange={(item) =>
                                            formik.setFieldValue(
                                              `ExpenseInvoiceItems[${index}].WarehouseId`,
                                              Number(item && item.value)
                                            )
                                          }
                                        />
                                      </td>
                                    )}

                                    <td className='ps-0'>
                                      <input
                                        type='number'
                                        className='form-control form-control-solid'
                                        autoComplete='off'
                                        disabled={formik.isSubmitting}
                                        placeholder={intl.formatMessage({
                                          id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.AMOUNT',
                                        })}
                                        name={`ExpenseInvoiceItems[${index}].Amount`}
                                        value={formik.values.ExpenseInvoiceItems[index].Amount}
                                        required
                                        onChange={(event) =>
                                          onChangeItem('Amount', Number(event.target.value), index)
                                        }
                                        onFocus={(event) => event.target.select()}
                                      />

                                      {visibiltyItem[index] && visibiltyItem[index].Discount && (
                                        <div className='input-group mt-2'>
                                          <input
                                            type='number'
                                            className='form-control form-control-solid'
                                            autoComplete='off'
                                            disabled={formik.isSubmitting}
                                            placeholder={intl.formatMessage({
                                              id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.DISCOUNTRATE',
                                            })}
                                            name={`ExpenseInvoiceItems[${index}].DiscountRate`}
                                            value={
                                              formik.values.ExpenseInvoiceItems[index].DiscountRate
                                            }
                                            onChange={(event) =>
                                              onChangeItem(
                                                'DiscountRate',
                                                Number(event.target.value),
                                                index
                                              )
                                            }
                                            onFocus={(event) => event.target.select()}
                                          />
                                          <Select
                                            data={DiscountType}
                                            name={`ExpenseInvoiceItems[${index}].DiscountTypeId`}
                                            value={
                                              formik.values.ExpenseInvoiceItems[index]
                                                .DiscountTypeId
                                            }
                                            required
                                            onChange={(item) =>
                                              onChangeItem(
                                                'DiscountTypeId',
                                                Number(item && item.value),
                                                index
                                              )
                                            }
                                          />
                                        </div>
                                      )}
                                    </td>

                                    <td className='ps-0'>
                                      <Select
                                        data={ProductUnit}
                                        name={`ExpenseInvoiceItems[${index}].ProductUnitId`}
                                        value={
                                          formik.values.ExpenseInvoiceItems[index].ProductUnitId
                                        }
                                        required
                                        onChange={(item) =>
                                          onChangeItem('ProductUnitId', Number(item && item.value), index)
                                        }
                                      />

                                      {visibiltyItem[index] && visibiltyItem[index].WitholdingRate && (
                                        <Select
                                          data={WitholdingType}
                                          className={'mt-2'}
                                          name={`ExpenseInvoiceItems[${index}].WitholdingRateId`}
                                          value={
                                            formik.values.ExpenseInvoiceItems[index]
                                              .WitholdingRateId
                                          }
                                          required
                                          onChange={(item) =>
                                            onChangeItem(
                                              'WitholdingRateId',
                                              Number(item && item.value),
                                              index
                                            )
                                          }
                                        />
                                      )}
                                    </td>

                                    <td className='ps-0'>
                                      <InputPrice
                                        placeholder={intl.formatMessage({
                                          id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.UNITPRICE',
                                        })}
                                        name={`ExpenseInvoiceItems[${index}].UnitPrice`}
                                        required
                                        value={formik.values.ExpenseInvoiceItems[index].UnitPrice}
                                        onChange={(value) =>
                                          onChangeItem('UnitPrice', Number(value), index)
                                        }
                                      />
                                    </td>

                                    <td className='ps-0'>
                                      <Select
                                        data={TaxType}
                                        name={`ExpenseInvoiceItems[${index}].TaxId`}
                                        value={formik.values.ExpenseInvoiceItems[index].TaxId}
                                        required
                                        onChange={(item) =>
                                          onChangeItem('TaxId', Number(item && item.value), index)
                                        }
                                      />
                                    </td>

                                    <td className='ps-0'>
                                      <InputPrice
                                        placeholder={intl.formatMessage({
                                          id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.TOTALPRICE',
                                        })}
                                        name={`ExpenseInvoiceItems[${index}].TotalPrice`}
                                        value={formik.values.ExpenseInvoiceItems[index].TotalPrice}
                                        onChange={(value) =>
                                          onChangeItem('TotalPrice', Number(value), index)
                                        }
                                      />
                                    </td>

                                    <td className='pt-5 text-end'>
                                      <div className='d-flex'>
                                        <Dropdown autoClose='outside'>
                                          <Dropdown.Toggle
                                            variant='link'
                                            className='btn btn-sm btn-icon btn-active-color-info'
                                          >
                                            <KTSVG
                                              path='/images/icons/duotune/arrows/arr009.svg'
                                              className='svg-icon-2'
                                            />
                                          </Dropdown.Toggle>

                                          <Dropdown.Menu>
                                            <Dropdown.Item
                                              onClick={() => onClickVisibityItem('Discount', index)}
                                              className={classNames({
                                                active: visibiltyItem[index] && visibiltyItem[index].Discount,
                                              })}
                                            >
                                              {intl.formatMessage({
                                                id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.DISCOUNT',
                                              })}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              onClick={() =>
                                                onClickVisibityItem('Description', index)
                                              }
                                              className={classNames({
                                                active: visibiltyItem[index] && visibiltyItem[index].Description,
                                              })}
                                            >
                                              {intl.formatMessage({
                                                id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.DESCRIPTION',
                                              })}
                                            </Dropdown.Item>
                                            <Dropdown.Item
                                              onClick={() =>
                                                onClickVisibityItem('WitholdingRate', index)
                                              }
                                              className={classNames({
                                                active: visibiltyItem[index] && visibiltyItem[index].WitholdingRate,
                                              })}
                                            >
                                              {intl.formatMessage({
                                                id: 'EXPENSEINVOICE.CREATEDETAIL.EXPENSEINVOICEITEMS.WITHOLDINGRATEID',
                                              })}
                                            </Dropdown.Item>
                                          </Dropdown.Menu>
                                        </Dropdown>
                                        <button
                                          type='button'
                                          className='btn btn-sm btn-icon btn-active-color-primary'
                                          onClick={() => arrayHelpers.remove(index)}
                                        >
                                          <KTSVG
                                            path='/images/icons/duotune/general/gen027.svg'
                                            className='svg-icon-2'
                                          />
                                        </button>
                                      </div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              <tfoot>
                                <tr className='border-top border-top-dashed align-middle fs-6 fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}>
                                    <button
                                      type='button'
                                      className='btn btn-sm btn-outline btn-outline-dashed btn-outline-success btn-active-light-success'
                                      onClick={() => onClickNewItem(arrayHelpers)}
                                    >
                                      <KTSVG
                                        path='/images/icons/duotune/arrows/arr009.svg'
                                        className='svg-icon-2'
                                      />
                                      {intl.formatMessage({id: 'ADD_NEW_LINE'})}
                                    </button>
                                  </th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.SUBTOTALPRICE',
                                    })}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.SubTotalPrice, {
                                      style: 'currency',
                                      currency: 'try',
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>

                                {formik.values.TotalDiscount > 0 && (
                                  <tr className='align-middle fw-bold text-gray-700'>
                                    <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                    <th
                                      className='border-bottom border-bottom-dashed ps-0 fs-5'
                                      colSpan={2}
                                    >
                                      {intl.formatMessage({
                                        id: 'EXPENSEINVOICE.CREATEDETAIL.TOTALDISCOUNT',
                                      })}
                                    </th>
                                    <th className='border-bottom border-bottom-dashed text-end'>
                                      {intl.formatNumber(formik.values.TotalDiscount, {
                                        style: 'currency',
                                        currency: 'try',
                                      })}
                                    </th>
                                    <th>&nbsp;</th>
                                  </tr>
                                )}

                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.GROSSTOTALPRICE',
                                    })}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.GrossTotalPrice, {
                                      style: 'currency',
                                      currency: 'try',
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>

                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th
                                    className='border-bottom border-bottom-dashed ps-0 fs-5'
                                    colSpan={2}
                                  >
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.TOTALTAX',
                                    })}
                                  </th>
                                  <th className='border-bottom border-bottom-dashed text-end'>
                                    {intl.formatNumber(formik.values.TotalTax, {
                                      style: 'currency',
                                      currency: 'try',
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>

                                {formik.values.TotalWitholding > 0 && (
                                  <tr className='align-middle fw-bold text-gray-700'>
                                    <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                    <th
                                      className='border-bottom border-bottom-dashed ps-0 fs-5'
                                      colSpan={2}
                                    >
                                      {intl.formatMessage({
                                        id: 'EXPENSEINVOICE.CREATEDETAIL.TOTALWITHOLDING',
                                      })}
                                    </th>
                                    <th className='border-bottom border-bottom-dashed text-end'>
                                      {intl.formatNumber(formik.values.TotalWitholding, {
                                        style: 'currency',
                                        currency: 'try',
                                      })}
                                    </th>
                                    <th>&nbsp;</th>
                                  </tr>
                                )}

                                <tr className='align-middle fw-bold text-gray-700'>
                                  <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                  <th className='fs-4 ps-0' colSpan={2}>
                                    {intl.formatMessage({
                                      id: 'EXPENSEINVOICE.CREATEDETAIL.TOTALPRICE',
                                    })}
                                  </th>
                                  <th className='text-end fs-4 text-nowrap'>
                                    {intl.formatNumber(formik.values.TotalPrice, {
                                      style: 'currency',
                                      currency: 'try',
                                    })}
                                  </th>
                                  <th>&nbsp;</th>
                                </tr>

                                {formik.values.TotalWitholding > 0 && (
                                  <tr className='align-middle fw-bold text-gray-700'>
                                    <th colSpan={formik.values.InvoiceTypeId === 1 ? 4 : 3}></th>
                                    <th className='fs-4 ps-0' colSpan={2}>
                                      {intl.formatMessage({
                                        id: 'EXPENSEINVOICE.CREATEDETAIL.TOTALPRICEPLUSWITHHOLDING',
                                      })}
                                    </th>
                                    <th className='text-end fs-4 text-nowrap'>
                                      {intl.formatNumber(
                                        formik.values.TotalPrice + formik.values.TotalWitholding,
                                        {style: 'currency', currency: 'try', minimumFractionDigits: 2, maximumFractionDigits: 4}
                                      )}
                                    </th>
                                    <th>&nbsp;</th>
                                  </tr>
                                )}
                              </tfoot>
                            </table>
                          </div>
                        </>
                      )}
                    />

                    <div className='row pt-15'>
                      <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                        <button
                          className='btn btn-light'
                          onClick={() =>
                            navigate(`/${pageUrl[1]}/${pageUrl[2]}/${pageUrl[3]}/detail/${id}`)
                          }
                        >
                          {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                        </button>
                        {!(detail && detail.isGibInvoice) && (
                          <button
                            type='submit'
                            className='btn btn-primary'
                            data-kt-users-modal-action='submit'
                            disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                          >
                            <span className='indicator-label'>
                              {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                            </span>
                            {formik.isSubmitting && (
                              <span className='indicator-progress'>
                                {intl.formatMessage({id: 'PLEASE'})}
                                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                              </span>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </form>
                )}
              </FormikProvider>
            </KTCardBody>
          </KTCard>
        </div>
      </div>

      {(loading | updateLoading) > 0 && <Loading />}
    </>
  )
}
export {Edit}
